import type { LiveSheetItem } from "../../types/generated/live";
import { Box } from "../ui/box";
import { Icon } from "../ui/icon";
import { NumberField } from "../ui/number_field";
import { Term } from "../ui/term";
import { useSortable } from "../ui/use_sortable";
import { ACTION_ROW, Button } from "./controls";
import { formatCost, purseInCopper, roughCost } from "./catalog";
import { useInventoryEdits } from "./edits";
import { foldOf } from "./folds";
import { formatWeight } from "./format";
import { termInfo } from "./glossary";
import { ItemMenu } from "./item_menu";
import type { Mover } from "./mover";
import { interpolate, type Labels } from "./labels";
import { Mark } from "./pop";
import { useOpenPopup } from "./popups";
import { LoadTerm, Purse } from "./purse";
import type { InventoryView } from "./sheet_view";
import { TABLE_CELL, TABLE_HEAD } from "./styles";

const QUANTITY = "cs-num h-9 w-14 rounded-lg border border-line bg-surface-raised px-1 text-center text-base text-ink";

// `loadBusy`: a change is still on its way, so the load shown is the old one. `notify` toasts. A sheet's inventory has a
// load and equips; the campaign's, which brings its own `title` and `move` too, has neither (`view.derived` is absent).
export function Inventory({
	view,
	labels,
	locale,
	loadBusy,
	notify,
	title = labels.boxes.inventory,
	note,
	move,
}: {
	view: InventoryView;
	labels: Labels;
	locale: string;
	loadBusy: boolean;
	notify: (message: string) => void;
	title?: string;
	// A paragraph under the title.
	note?: string;
	// Where an item can go from here: a sheet's is its campaign, when it is in one (`sheetMoves`), the party's a sheet.
	move?: (item: LiveSheetItem) => Mover | null;
}) {
	const { sheet } = view;
	const edits = useInventoryEdits();
	const equips = Boolean(view.derived);
	const mover = (item: LiveSheetItem) => move?.(item) ?? null;
	const open = useOpenPopup();
	const items = new Map(view.inventory.map((item) => [item.id, item]));
	const sortable = useSortable({
		ids: view.inventory.map((item) => item.id),
		onReorder: (ids) => edits?.reorder(ids),
		announce: (id, position, total) => interpolate(labels.inventory.moved_to, { name: items.get(id)?.name ?? "", position, total }),
	});
	const coinCount = sheet.money_gp + sheet.money_sp + sheet.money_cp;
	const columns = 4 + (equips ? 1 : 0) + (edits ? 2 : 0);
	const money = { gp: labels.inventory.gold, sp: labels.inventory.silver, cp: labels.inventory.copper };
	return (
		<Box icon="backpack" title={title} fold={foldOf(view, "inventory", title, labels)} class="px-3 pb-3">
			{note && <p class="mb-2 text-sm text-ink-muted">{note}</p>}
			<Purse view={view} labels={labels} loadBusy={loadBusy} />
			<div class="overflow-x-auto">
				<table class="cs-table mt-1.5 w-full border-collapse text-[14.5px]">
					<thead>
						<tr>
							{edits && (
								<th class={`${TABLE_HEAD} w-8`}>
									<span class="sr-only">{labels.inventory.drag_column}</span>
								</th>
							)}
							{equips && (
								<th class={`${TABLE_HEAD} w-11 text-center`}>
									<Term label={labels.inventory.equipped_short} {...termInfo(labels, "equipped", labels.inventory.equipped)} />
								</th>
							)}
							<th class={TABLE_HEAD}>{labels.inventory.item}</th>
							<th class={TABLE_HEAD}>{labels.inventory.qty}</th>
							<th class={`${TABLE_HEAD} cs-narrow-hidden`}>
								<LoadTerm view={view} labels={labels} />
							</th>
							<th class={`${TABLE_HEAD} cs-narrow-hidden text-right`}>
								<Term label={labels.inventory.cost_total} {...termInfo(labels, "cost_total", labels.inventory.cost_total)} />
							</th>
							{edits && (
								<th class={`${TABLE_HEAD} w-10`}>
									<span class="sr-only">{labels.inventory.actions_column}</span>
								</th>
							)}
						</tr>
					</thead>
					<tbody>
						{view.inventory.length === 0 && (
							<tr>
								<td colSpan={columns} class={`${TABLE_CELL} italic text-ink-muted`}>
									{labels.inventory.empty}
								</td>
							</tr>
						)}
						{sortable.order.map((id) => {
							const item = items.get(id);
							if (!item) return null;
							const name = { name: item.name };
							return (
								<tr
									key={id}
									{...sortable.row(id)}
									class={sortable.dragging === id ? "bg-surface-sunken outline outline-2 -outline-offset-2 outline-dashed outline-line-strong" : ""}
								>
									{edits && (
										<td class={TABLE_CELL}>
											<button
												type="button"
												class={`cs-tap inline-flex h-8 w-7 touch-none items-center justify-center rounded-md text-ink-faint hover:bg-surface-sunken hover:text-ink ${sortable.dragging === id ? "cursor-grabbing" : "cursor-grab"}`}
												aria-label={interpolate(labels.inventory.drag, name)}
												{...sortable.grip(id)}
											>
												<Icon name="grip" class="h-3.5 w-3.5" />
											</button>
										</td>
									)}
									{equips && (
										<td class={`${TABLE_CELL} text-center`}>
											{!item.equippable ? null : edits ? (
												<input
													type="checkbox"
													class="h-[18px] w-[18px] accent-carmesim-600"
													checked={item.equipped}
													aria-label={interpolate(item.equipped ? labels.inventory.equipped_of : labels.inventory.unequipped_of, name)}
													onChange={(event) => edits.setEquipped(id, event.currentTarget.checked)}
												/>
											) : (
												<Mark on={item.equipped} label={item.equipped ? labels.inventory.equipped : labels.inventory.unequipped} />
											)}
										</td>
									)}
									<td class={`${TABLE_CELL} cs-break`}>
										<b>{item.name}</b>
										{item.magic_item && (
											<span class="ml-1 inline-flex align-[-2px]" title={labels.inventory.magic_item}>
												<Icon name="magicItem" class="h-4 w-4 text-dourado-600 dark:text-dourado-300" />
												<span class="sr-only">{labels.inventory.magic_item}</span>
											</span>
										)}
										{item.description && <div class="text-xs text-ink-muted">{item.description}</div>}
										<div class="cs-under-name text-xs text-ink-muted">
											{[
												item.cost != null && formatCost(item.total_cost ?? 0, money),
												item.total_weight_grams > 0 && formatWeight(item.total_weight_grams, locale),
											]
												.filter(Boolean)
												.join(" · ")}
										</div>
									</td>
									<td class={TABLE_CELL}>
										{edits ? (
											<NumberField
												value={item.quantity}
												min={1}
												label={interpolate(labels.inventory.quantity_of, name)}
												class={QUANTITY}
												onCommit={(next) => edits.setQuantity(id, next)}
											/>
										) : (
											item.quantity
										)}
									</td>
									<td class={`${TABLE_CELL} cs-narrow-hidden`}>{formatWeight(item.total_weight_grams, locale)}</td>
									<td class={`${TABLE_CELL} cs-narrow-hidden text-right`}>{item.cost == null ? "-" : formatCost(item.total_cost ?? 0, money)}</td>
									{edits && (
										<td class={TABLE_CELL}>{open && <ItemMenu item={item} labels={labels} mover={mover(item)} edits={edits} open={open} notify={notify} />}</td>
									)}
								</tr>
							);
						})}
						<tr>
							{edits && <td class={TABLE_CELL} />}
							{equips && <td class={TABLE_CELL} />}
							<td class={TABLE_CELL}>
								<b class="flex items-center gap-1.5">
									<Icon name="coins" class="h-4 w-4 text-ink-muted" />
									{labels.inventory.coins}
								</b>
								<div class="cs-under-name text-xs text-ink-muted">
									± {roughCost(purseInCopper(sheet), money)}
									{coinCount > 0 && ` · ${formatWeight(coinCount * 10, locale)}`}
								</div>
							</td>
							<td class={TABLE_CELL}>{coinCount}</td>
							<td class={`${TABLE_CELL} cs-narrow-hidden`}>{formatWeight(coinCount * 10, locale)}</td>
							<td class={`${TABLE_CELL} cs-narrow-hidden text-right`}>± {roughCost(purseInCopper(sheet), money)}</td>
							{edits && <td class={TABLE_CELL} />}
						</tr>
					</tbody>
				</table>
			</div>
			<div class="sr-only" aria-live="polite">
				{sortable.announcement}
			</div>
			{edits && open && (
				<div class={`mt-2 ${ACTION_ROW}`}>
					<Button
						tone="primary"
						icon="backpack"
						label={labels.catalog.title}
						attrs={{ "data-inventory-add": "" }}
						onClick={(button) => open({ kind: "catalog" }, button)}
					/>
					<Button tone="primary" icon="anvil" label={labels.item.new_title} onClick={(button) => open({ kind: "item_new" }, button)} />
				</div>
			)}
		</Box>
	);
}
